import { type Identity, parseAccountType, parseOrganizations } from "@cosmosai/shared";
import {
  type User,
  type UserManager,
  type UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";
import type { AuthPort } from "./auth-port";
import { realmRolesOf } from "./claims";

/** The part of oidc-client-ts's UserManager this adapter uses; fakes implement it in tests. */
export type UserManagerLike = Pick<
  UserManager,
  "getUser" | "signinRedirect" | "signinRedirectCallback" | "signinSilent" | "signoutRedirect"
>;

/** Authorization code with PKCE against Keycloak; tokens kept in sessionStorage, never localStorage. */
export function oidcSettings(
  oidc: { authority: string; clientId: string },
  origin: string,
  storage: Storage = window.sessionStorage,
): UserManagerSettings {
  return {
    authority: oidc.authority,
    client_id: oidc.clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/signed-out`,
    response_type: "code",
    scope: "openid profile email organization",
    userStore: new WebStorageStateStore({ store: storage }),
    stateStore: new WebStorageStateStore({ store: storage }),
    automaticSilentRenew: true,
  };
}

export class OidcAuth implements AuthPort {
  constructor(private readonly manager: UserManagerLike) {}

  async current(): Promise<Identity | null> {
    const user = await this.user();
    return user ? identityOf(user) : null;
  }

  async accessToken(): Promise<string | undefined> {
    return (await this.user())?.access_token;
  }

  async signIn(returnTo: string): Promise<void> {
    await this.manager.signinRedirect({ state: { returnTo } });
  }

  async completeSignIn(): Promise<string> {
    const user = await this.manager.signinRedirectCallback();
    const state = user.state as { returnTo?: unknown } | undefined;
    return typeof state?.returnTo === "string" ? state.returnTo : "/";
  }

  async signOut(): Promise<void> {
    await this.manager.signoutRedirect();
  }

  /** The stored user, renewed with the refresh token once expired; null when that fails. */
  private async user(): Promise<User | null> {
    const user = await this.manager.getUser();
    if (!user?.expired) return user;
    return this.manager.signinSilent().catch(() => null);
  }
}

/** Name, email and organizations from the ID token; the account type from the access token. */
export function identityOf(user: Pick<User, "profile" | "access_token">): Identity {
  const { profile } = user;
  return {
    sub: profile.sub,
    ...(profile.name ? { name: profile.name } : {}),
    ...(profile.email ? { email: profile.email } : {}),
    organizations: parseOrganizations(profile.organization),
    // Display only: the APIs read the type from the verified token.
    accountType: parseAccountType(realmRolesOf(user.access_token)),
  };
}
